import { useEffect } from 'react';

// Polls the server's index.html for the current bundle filename and reloads
// the page if a newer bundle is being served than the one currently running.
// Without this, a long-lived kiosk tab can hold an in-memory copy of OLD JS
// for days even after a deploy, because SPA navigation never re-fetches
// index.html and the browser's heuristic cache keeps the in-memory script
// alive. This watcher closes that window: any time the tab regains focus
// or the periodic timer fires, we revalidate index.html (server sends
// Cache-Control: no-cache), parse the bundle hash, and reload if it changed.

const POLL_MS = 5 * 60 * 1000; // 5 minutes
const BUNDLE_RE = /main\.([a-f0-9]+)\.chunk\.js/;

function currentBundleHash(): string | null {
    const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
    for (const s of scripts) {
        const m = s.src.match(BUNDLE_RE);
        if (m) return m[1];
    }
    return null;
}

async function latestBundleHash(): Promise<string | null> {
    try {
        const res = await fetch('/?_vc=' + Date.now(), { cache: 'no-store' });
        if (!res.ok) return null;
        const html = await res.text();
        const m = html.match(BUNDLE_RE);
        return m ? m[1] : null;
    } catch {
        return null;
    }
}

const VersionWatcher = () => {
    useEffect(() => {
        const current = currentBundleHash();
        if (!current) return; // dev mode or pre-build, skip

        let stopped = false;

        const check = async () => {
            if (stopped) return;
            const latest = await latestBundleHash();
            if (latest && latest !== current) {
                // eslint-disable-next-line no-console
                console.log('[version-watcher] new bundle ' + latest + ' (was ' + current + ') — reloading');
                window.location.reload();
            }
        };

        const onVisible = () => {
            if (document.visibilityState === 'visible') check();
        };

        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', check);
        const timerId = window.setInterval(check, POLL_MS);

        // First check shortly after mount (don't block render)
        const bootId = window.setTimeout(check, 30_000);

        return () => {
            stopped = true;
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', check);
            window.clearInterval(timerId);
            window.clearTimeout(bootId);
        };
    }, []);

    return null;
};

export default VersionWatcher;
